// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Communication, Patient, Reference, Resource } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

export type SenderMap = Map<string, Resource>;

export interface UsePatientCommunicationsResult {
  communications: Communication[];
  senders: SenderMap;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Loads every `Communication` where the patient is a recipient, sorted newest
 * first, and resolves each distinct `sender` reference once so the table can
 * render the sender's display name without doing N+1 fetches.
 */
export function usePatientCommunications(patient: Patient | undefined): UsePatientCommunicationsResult {
  const medplum = useMedplum();
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [senders, setSenders] = useState<SenderMap>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const patientRef = `Patient/${patient.id}`;
      const [recipientResults, subjectResults] = await Promise.all([
        medplum.searchResources('Communication', {
          recipient: patientRef,
          _sort: '-sent',
          _count: '100',
        }) as Promise<Communication[]>,
        medplum.searchResources('Communication', {
          subject: patientRef,
          _sort: '-sent',
          _count: '100',
        }) as Promise<Communication[]>,
      ]);
      const byId = new Map<string, Communication>();
      for (const communication of [...recipientResults, ...subjectResults]) {
        byId.set(communication.id ?? JSON.stringify(communication), communication);
      }
      const list = Array.from(byId.values()).sort(
        (a, b) =>
          new Date(b.sent ?? b.meta?.lastUpdated ?? 0).getTime() -
          new Date(a.sent ?? a.meta?.lastUpdated ?? 0).getTime()
      );
      setCommunications(list);

      const uniqueSenderRefs = Array.from(
        new Set(
          list.map((c) => (c.sender as Reference | undefined)?.reference).filter((ref): ref is string => Boolean(ref))
        )
      );

      const resolved = await Promise.all(
        uniqueSenderRefs.map(
          async (ref): Promise<[string, Resource | undefined]> => [
            ref,
            await medplum.readReference({ reference: ref }).catch(() => undefined),
          ]
        )
      );

      const nextSenders: SenderMap = new Map();
      for (const [ref, resource] of resolved) {
        if (resource) {
          nextSenders.set(ref, resource);
        }
      }
      setSenders(nextSenders);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load communications'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { communications, senders, loading, error, refresh: fetch };
}
